"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthManager = exports.AUTH_STATUS_TYPE = exports.TokenParseFailedError = void 0;
const mutex_helper_1 = require("../helpers/mutex.helper");
const DEFAULT_REQUEST_HEADERS_1 = require("../constants/DEFAULT_REQUEST_HEADERS");
const retry_1 = require("../helpers/retry");
const HTTP_METHOD_1 = require("../constants/HTTP_METHOD");
const api_json_response_1 = require("../utils/api.json.response");
const use_fetch_helper_1 = require("../helpers/use-fetch.helper");
class TokenParseFailedError extends Error {
}
exports.TokenParseFailedError = TokenParseFailedError;
/**
 * Refresh credentials if they are to expire within the next `n` minutes
 */
const DEFAULT_LOGIN_RETRY_COUNT = 3; // 3 retries
const DEFAULT_LOGIN_RETRY_AFTER_MILLIS = 500; // 0.5 seconds
class LoggedInPayload {
    constructor(token, config) {
        this.token = token;
        this.config = config;
        //
    }
    /**
     * Are the credentials expired outside the acceptable time window?
     */
    isSoftExpired() {
        const expiresAtMs = this.token.retrievedAtUtc + (this.token.expires_in * 1000);
        const nowMs = Date.now();
        const isSoftExpired = expiresAtMs <= nowMs + (this.config.authRefreshWindow);
        return isSoftExpired;
    }
    /**
     * Are the credentials expired past their final date?
     */
    isHardExpired() {
        const expiresAtMs = (this.token.retrievedAtUtc + this.token.expires_in * 1000);
        const nowMs = Date.now();
        const isHardExpired = expiresAtMs <= nowMs;
        return isHardExpired;
    }
}
/**
 * STATUS
 */
exports.AUTH_STATUS_TYPE = {
    UNINITIALISED: 'UNINITIALISED',
    LOGGING_IN: 'LOGGING_IN',
    LOGGED_IN: 'LOGGED_IN',
    REFRESHING: 'REFRESHING',
    FAILED: 'FAILED',
};
class AuthManager {
    /**
     * @constructor
     */
    constructor(options, authEvents) {
        var _a, _b, _c, _d, _e;
        this.authEvents = authEvents;
        this._mutex = { credentials: new mutex_helper_1.Mutex(), };
        this.config = options.config;
        if (options.authToken) {
            this._status = {
                type: exports.AUTH_STATUS_TYPE.LOGGED_IN,
                payload: new LoggedInPayload(options.authToken, this.config),
            };
        }
        else {
            this._status = {
                type: exports.AUTH_STATUS_TYPE.UNINITIALISED,
                payload: null
            };
        }
        this._client = options.client;
        this._loginDetails = options.loginDetails;
        this._retryOptions = {
            afterMillis: (_b = (_a = options.retryOptions) === null || _a === void 0 ? void 0 : _a.afterMillis) !== null && _b !== void 0 ? _b : DEFAULT_LOGIN_RETRY_AFTER_MILLIS,
            count: (_d = (_c = options.retryOptions) === null || _c === void 0 ? void 0 : _c.count) !== null && _d !== void 0 ? _d : DEFAULT_LOGIN_RETRY_COUNT,
        };
        this._verbose = (_e = options.verbose) !== null && _e !== void 0 ? _e : false;
        this._logger = options.logger;
    }
    get status() {
        return this._status;
    }
    /**
     * Is the auth manager in a failed state?
     */
    isFailed() {
        return this._status.type === exports.AUTH_STATUS_TYPE.FAILED;
    }
    /**
     * Reset authentication
     *
     * Allows resetting of failed state back to uninitialised
     */
    logout() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._verbose) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug('[AuthManager::reset] Resetting authentication....');
            }
            const unlock = yield this._mutex.credentials.lock();
            try {
                switch (this._status.type) {
                    case exports.AUTH_STATUS_TYPE.LOGGING_IN:
                    case exports.AUTH_STATUS_TYPE.REFRESHING:
                        throw new Error('Invalid state: logging out credentials...');
                    case exports.AUTH_STATUS_TYPE.FAILED:
                        // do nothing
                        break;
                    case exports.AUTH_STATUS_TYPE.UNINITIALISED:
                        return;
                    case exports.AUTH_STATUS_TYPE.LOGGED_IN:
                        try {
                            yield this._logOut();
                        }
                        catch (error) {
                            if (this._verbose)
                                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.error(`[logout] Errored logging out "${JSON.stringify(error, null, 2)}"`);
                            // do nothing
                        }
                        break;
                    default:
                        break;
                }
                if (this._verbose) {
                    (_c = this._logger) === null || _c === void 0 ? void 0 : _c.debug('[AuthManager::reset::try] Acquired credentials lock....');
                }
                this._setStatus({ type: exports.AUTH_STATUS_TYPE.UNINITIALISED, payload: null });
            }
            finally {
                if (this._verbose) {
                    (_d = this._logger) === null || _d === void 0 ? void 0 : _d.debug('[AuthManager::reset::finally] Releasing credentials lock...');
                }
                yield unlock();
            }
        });
    }
    /**
     * Reset authentication
     *
     * Allows resetting of failed state back to uninitialised
     */
    reset() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._verbose) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug('[AuthManager::reset] Resetting authentication....');
            }
            // don't need to do nuffin
            if (this.status.type === exports.AUTH_STATUS_TYPE.UNINITIALISED)
                return;
            const unlock = yield this._mutex.credentials.lock();
            try {
                if (this._verbose) {
                    (_b = this._logger) === null || _b === void 0 ? void 0 : _b.debug('[AuthManager::reset::try] Acquired credentials lock....');
                }
                this._setStatus({ type: exports.AUTH_STATUS_TYPE.UNINITIALISED, payload: null });
                return;
            }
            finally {
                if (this._verbose) {
                    (_c = this._logger) === null || _c === void 0 ? void 0 : _c.debug('[AuthManager::reset::finally] Releasing credentials lock...');
                }
                yield unlock();
            }
        });
    }
    /**
     * Retrieve credentials
     *
     * Log-in or refresh-login if required
     */
    credentials() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._verbose) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug('[AuthManager::credentials] Getting credentials....');
            }
            const unlock = yield this._mutex.credentials.lock();
            try {
                if (this._verbose) {
                    (_b = this._logger) === null || _b === void 0 ? void 0 : _b.debug('[AuthManager::credentials] Acquired credentials lock....');
                }
                const status = this._status;
                let authToken;
                switch (status.type) {
                    // if logging in or refreshing, we should be waiting for the mutex to give us a lock...
                    // we should only be here if LOGGED IN, FAILED, or UNINITIALISED
                    case exports.AUTH_STATUS_TYPE.LOGGING_IN:
                    case exports.AUTH_STATUS_TYPE.REFRESHING:
                        throw new Error('Invalid state: logging-in or refreshing credentials...');
                    // if failed, throw. require the caller to reset if they want to retry
                    case exports.AUTH_STATUS_TYPE.FAILED:
                        throw status.payload.error;
                    // refresh or give token
                    case exports.AUTH_STATUS_TYPE.LOGGED_IN: {
                        // If the token is still considered valid, assume it's ok to use.
                        if (!status.payload.isSoftExpired()) {
                            return status.payload.token;
                        }
                        // Otherwise, attempt to refresh the token
                        if (this._verbose) {
                            (_c = this._logger) === null || _c === void 0 ? void 0 : _c.debug('[AuthManager::credentials::try] Refreshing credentials...');
                        }
                        this._setStatus({ type: exports.AUTH_STATUS_TYPE.REFRESHING, payload: status.payload });
                        let result;
                        try {
                            result = yield retry_1.retry(this._retryOptions, { logger: this._logger, verbose: this._verbose })(() => this._refreshLogin(status.payload.token));
                        }
                        catch (error) {
                            // if errored because failed to parse token, panic
                            if (error instanceof TokenParseFailedError) {
                                throw error;
                            }
                        }
                        if (result && result.success) {
                            authToken = result.payload;
                        }
                        else {
                            // errored trying to _refreshLogin
                            // TODO: are there other statuses indicating the credentials have fully been invalidated?
                            if (result && (result.payload.status === 401)) {
                                if (this._verbose) {
                                    (_d = this._logger) === null || _d === void 0 ? void 0 : _d.warn(`[AuthManager::credentials::try] Failed refreshing token, received status "${401}"...`);
                                }
                                // credentials have been rejected...
                                throw new Error('Credentials have been invalidated');
                            }
                            // credentials fetching failed
                            if (status.payload.isHardExpired()) {
                                if (this._verbose) {
                                    (_e = this._logger) === null || _e === void 0 ? void 0 : _e.warn('[AuthManager::credentials::try] Failed refreshing token, token is hard expired...');
                                }
                                // credentials have expired and we can't use the old ones :(
                                throw new Error('Credentials have expired and can no longer be used');
                            }
                            // An unexpected error occurred trying to refresh the auth token, keep using the old token while we can...
                            // using old token... return instead of resetting below
                            return status.payload.token;
                        }
                        break;
                    }
                    // log-in
                    case exports.AUTH_STATUS_TYPE.UNINITIALISED: {
                        this._setStatus({ type: exports.AUTH_STATUS_TYPE.LOGGING_IN, payload: null, });
                        if (this._verbose) {
                            (_f = this._logger) === null || _f === void 0 ? void 0 : _f.debug('[AuthManager::credentials::try] Logging in...');
                        }
                        const result = yield retry_1.retry(this._retryOptions, { logger: this._logger, verbose: this._verbose })(() => this._login());
                        if (!result.success)
                            throw new Error(`${result.payload.body.error} ${result.payload.body.message}`);
                        authToken = result.payload;
                        break;
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    default: throw new Error(`Unhandled status type: "${status.type}"`);
                }
                if (this._verbose) {
                    (_g = this._logger) === null || _g === void 0 ? void 0 : _g.debug('[AuthManager::credentials::try] Successfully updated credentials');
                }
                this._setStatus({
                    type: exports.AUTH_STATUS_TYPE.LOGGED_IN,
                    payload: new LoggedInPayload(authToken, this.config),
                });
                return authToken;
            }
            catch (error) {
                if (this._verbose) {
                    (_h = this._logger) === null || _h === void 0 ? void 0 : _h.error(`[AuthManager::credentials::catch] Errored getting credentials: "${error && error.toString()}"...`);
                }
                this._setStatus({
                    type: exports.AUTH_STATUS_TYPE.FAILED,
                    payload: { error: error instanceof Error ? error : new Error(String(error)), },
                });
                throw error;
            }
            finally {
                if (this._verbose) {
                    (_j = this._logger) === null || _j === void 0 ? void 0 : _j.debug('[AuthManager::credentials::finally] Releasing credentials lock...');
                }
                yield unlock();
            }
        });
    }
    /**
     * Log in under new user details
     * Remove previous user details
     */
    rebaseLogin(arg) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._verbose) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug('[AuthManager::rebaseLogin] Resetting authentication....');
            }
            const unlock = yield this._mutex.credentials.lock();
            if (this._verbose) {
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.debug('[AuthManager::rebaseLogin] Acquired credentials lock....');
            }
            this._loginDetails = arg.storeUserLogin ? arg.login : undefined;
            try {
                this._setStatus({ type: exports.AUTH_STATUS_TYPE.LOGGING_IN, payload: null, });
                if (this._verbose) {
                    (_c = this._logger) === null || _c === void 0 ? void 0 : _c.debug('[AuthManager::rebaseLogin::try] Logging in...');
                }
                const result = yield retry_1.retry(this._retryOptions, { logger: this._logger, verbose: this._verbose })(() => this._login(arg.login));
                if (!result.success) {
                    if (this._verbose) {
                        (_d = this._logger) === null || _d === void 0 ? void 0 : _d.warn('[AuthManager::rebaseLogin::try] Failed to rebase login');
                    }
                    this._setStatus({
                        type: exports.AUTH_STATUS_TYPE.FAILED,
                        payload: { error: new Error(`${result.payload.body.error} ${result.payload.body.message}`) },
                    });
                    return result;
                }
                if (this._verbose) {
                    (_e = this._logger) === null || _e === void 0 ? void 0 : _e.debug('[AuthManager::rebaseLogin::try] Successfully updated credentials');
                }
                this._setStatus({
                    type: exports.AUTH_STATUS_TYPE.LOGGED_IN,
                    payload: new LoggedInPayload(result.payload, this.config),
                });
                return result;
            }
            catch (error) {
                if (this._verbose) {
                    (_f = this._logger) === null || _f === void 0 ? void 0 : _f.warn('[AuthManager::rebaseLogin::catch] Errored');
                }
                this._setStatus({
                    type: exports.AUTH_STATUS_TYPE.FAILED,
                    payload: { error: error instanceof Error ? error : new Error(String(error)), },
                });
                throw error;
            }
            finally {
                if (this._verbose) {
                    (_g = this._logger) === null || _g === void 0 ? void 0 : _g.debug('[AuthManager::reset::finally] Releasing credentials lock...');
                }
                yield unlock();
            }
        });
    }
    /**
     * Log out
     */
    _logOut(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let headers = DEFAULT_REQUEST_HEADERS_1.DEFAULT_REQUEST_HEADERS;
            const url = this.config.urlWithVersion('/logout');
            if (this.status.payload instanceof LoggedInPayload) {
                headers = Object.assign(Object.assign({}, headers), { 'Authorization': `Bearer ${this.status.payload.token.access_token}` });
            }
            // eslint-disable-next-line @typescript-eslint/ban-types
            const response = yield use_fetch_helper_1.useFetch(url, Object.assign({ method: HTTP_METHOD_1.HTTP_METHOD.POST, headers }, opts));
            const json = yield response.json();
            const jsonResponse = new api_json_response_1.ApiJsonResponse(response, json);
            return jsonResponse;
        });
    }
    /**
     * Log in
     */
    _login(arg) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = arg === null || arg === void 0 ? void 0 : arg.email) !== null && _a !== void 0 ? _a : (_b = this._loginDetails) === null || _b === void 0 ? void 0 : _b.email) || (!((_c = arg === null || arg === void 0 ? void 0 : arg.password) !== null && _c !== void 0 ? _c : (_d = this._loginDetails) === null || _d === void 0 ? void 0 : _d.password))) {
                throw new TypeError('[AuthManager::_login] Both email and password must be specified or pre-configured before login can be performed.');
            }
            const url = this.config.urlWithoutVersion('/oauth/token');
            const headers = DEFAULT_REQUEST_HEADERS_1.DEFAULT_REQUEST_HEADERS;
            const method = HTTP_METHOD_1.HTTP_METHOD.POST;
            const body = {
                username: (_e = arg === null || arg === void 0 ? void 0 : arg.email) !== null && _e !== void 0 ? _e : (_f = this._loginDetails) === null || _f === void 0 ? void 0 : _f.email,
                password: (_g = arg === null || arg === void 0 ? void 0 : arg.password) !== null && _g !== void 0 ? _g : (_h = this._loginDetails) === null || _h === void 0 ? void 0 : _h.password,
                grant_type: 'password',
                client_id: this._client.client_id,
                client_secret: this._client.client_secret,
                scope: '*',
            };
            const response = yield use_fetch_helper_1.useFetch(url, { method, body: JSON.stringify(body), headers });
            const json = yield response.json();
            const jsonResponse = new api_json_response_1.ApiJsonResponse(response, json);
            if (!jsonResponse.ok) {
                return {
                    success: false,
                    payload: {
                        status: jsonResponse.status,
                        body: {
                            error: (_k = (_j = jsonResponse.body) === null || _j === void 0 ? void 0 : _j.error) !== null && _k !== void 0 ? _k : '???',
                            message: (_m = (_l = jsonResponse.body) === null || _l === void 0 ? void 0 : _l.message) !== null && _m !== void 0 ? _m : '???'
                        }
                    }
                };
            }
            const parse = this._parseToken(jsonResponse.body, new Date());
            if (!parse.isValid) {
                if (this._verbose) {
                    (_o = this._logger) === null || _o === void 0 ? void 0 : _o.warn(`[AuthManager::_login] Failed to parse authToken... "${parse.payload.toString()}"`);
                }
                throw parse.payload;
            }
            const authToken = parse.payload;
            return { success: true, payload: authToken };
        });
    }
    /**
     * Refresh login
     */
    _refreshLogin(oldToken) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.config.urlWithoutVersion('/oauth/token');
            const headers = DEFAULT_REQUEST_HEADERS_1.DEFAULT_REQUEST_HEADERS;
            const method = HTTP_METHOD_1.HTTP_METHOD.POST;
            const body = {
                refresh_token: oldToken.refresh_token,
                grant_type: 'refresh_token',
                client_id: this._client.client_id,
                client_secret: this._client.client_secret,
                scope: '',
            };
            // eslint-disable-next-line @typescript-eslint/ban-types
            const response = yield use_fetch_helper_1.useFetch(url, { method, body: JSON.stringify(body), headers });
            const json = yield response.json();
            const jsonResponse = new api_json_response_1.ApiJsonResponse(response, json);
            // in refresh, simply throw if the response is bad
            if (!jsonResponse.ok) {
                return {
                    success: false,
                    payload: {
                        status: jsonResponse.status,
                        body: {
                            error: (_b = (_a = jsonResponse.body) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : '???',
                            message: (_d = (_c = jsonResponse.body) === null || _c === void 0 ? void 0 : _c.message) !== null && _d !== void 0 ? _d : '???'
                        }
                    }
                };
            }
            const parse = this._parseToken(jsonResponse.body, new Date());
            if (!parse.isValid) {
                if (this._verbose) {
                    (_e = this._logger) === null || _e === void 0 ? void 0 : _e.warn(`[AuthManager::_refresh] Failed to parse authToken... "${parse.payload.toString()}"`);
                }
                throw parse.payload;
            }
            const authToken = parse.payload;
            return { success: true, payload: authToken, };
        });
    }
    /**
     * Validate a token payload
     */
    _parseToken(unknown, retrievedAt) {
        if (typeof unknown !== 'object') {
            return { isValid: false, payload: new Error('Failed to validate token: payload is not an object') };
        }
        if (unknown === null) {
            return { isValid: false, payload: new Error('Failed to validate token: payload null') };
        }
        const validatingToken = {
            token_type: unknown.token_type,
            access_token: unknown.access_token,
            expires_in: unknown.expires_in,
            refresh_token: unknown.refresh_token,
        };
        const errors = [];
        if (validatingToken.token_type !== 'Bearer')
            errors.push('"token_type" is not "Bearer"');
        if (typeof validatingToken.access_token !== 'string')
            errors.push('"access_token" is not a "string"');
        if (typeof validatingToken.expires_in !== 'number')
            errors.push('"expires_in" is not a "number"');
        if (typeof validatingToken.refresh_token !== 'string')
            errors.push('"refresh_token" is not a "string"');
        if (errors.length) {
            return { isValid: false, payload: new TokenParseFailedError(`Failed to validate token: ${errors.join(', ')}`), };
        }
        return { isValid: true, payload: Object.assign(Object.assign({}, validatingToken), { retrievedAtUtc: retrievedAt.valueOf() }) };
    }
    /**
     * Update the status of the Auth Manager
     */
    _setStatus(newStatus) {
        const oldType = this._status.type;
        const oldToken = this._status.type === (exports.AUTH_STATUS_TYPE.LOGGED_IN || exports.AUTH_STATUS_TYPE.REFRESHING) ? this._status.payload.token : null;
        this._status = newStatus;
        // Only broadcast the Status Change if the status has actually changed
        if (newStatus.type !== oldType) {
            this.authEvents.emit('AUTH_STATUS_CHANGE', { newStatus });
        }
        // Only broadcast the Token Change if the token has actually changed
        const newToken = newStatus.type === (exports.AUTH_STATUS_TYPE.LOGGED_IN || exports.AUTH_STATUS_TYPE.REFRESHING) ? newStatus.payload.token : null;
        if (oldToken !== newToken) {
            this.authEvents.emit('AUTH_TOKEN_CHANGE', { newToken });
        }
    }
    /**
     * TODO: move this into an auth api file that somehow doesn't use the auth manager
     * since this route is not password protected
     */
    changePassword(body) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.config.urlWithVersion('/password/reset');
            const method = HTTP_METHOD_1.HTTP_METHOD.POST;
            const headers = DEFAULT_REQUEST_HEADERS_1.DEFAULT_REQUEST_HEADERS;
            // leave error handling to client side
            // will throw on network errors
            // won't throw on 400 range
            const response = yield use_fetch_helper_1.useFetch(url, { method, body: JSON.stringify(body), headers });
            const json = yield response.json();
            const jsonResponse = new api_json_response_1.ApiJsonResponse(response, json);
            return jsonResponse;
        });
    }
    /**
     * TODO: move this into an auth api file that somehow doesn't use the auth manager
     * since this route is not password protected
     */
    requestPasswordChangeEmail(body) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.config.urlWithVersion('/password/email');
            const method = HTTP_METHOD_1.HTTP_METHOD.POST;
            const headers = DEFAULT_REQUEST_HEADERS_1.DEFAULT_REQUEST_HEADERS;
            // leave error handling to client side
            // will throw on network errors
            // won't throw on 400 range
            const response = yield use_fetch_helper_1.useFetch(url, { method, body: JSON.stringify(body), headers });
            const json = yield response.json();
            const jsonResponse = new api_json_response_1.ApiJsonResponse(response, json);
            return jsonResponse;
        });
    }
}
exports.AuthManager = AuthManager;
